<template>
  <div fluid class="pa-0 pa-md-4 fill-height align-start d-flex flex-column cc-root">
    <!-- ============ STATS HERO (Desktop) ============ -->
    <v-card v-if="canViewAll && !loading && $vuetify.breakpoint.mdAndUp" class="mb-4 w-100 rounded-xl stats-hero" flat dark>
      <div class="stats-hero-glow"></div>
      <div class="pa-5 d-flex align-center position-relative" style="z-index: 2">
        <div class="mr-8 flex-shrink-0">
          <div class="caption text-uppercase font-weight-bold letter-spaced muted-text">
            <v-icon x-small color="primary" class="mr-1">mdi-credit-card-outline</v-icon>
            Gesamt {{ dateDisplay }}
          </div>
          <div class="display-1 font-weight-black mt-1 amount-display">
            <span class="currency-prefix">CHF</span>
            <span class="amount-number">{{ animatedTotal }}</span>
          </div>
          <div class="caption muted-text mt-1">{{ bills.length }} {{ bills.length === 1 ? "Beleg" : "Belege" }} · Ø CHF {{ averageAmount }}</div>
        </div>
        <v-divider vertical class="mx-2 divider-glow"></v-divider>
        <div class="d-flex align-center overflow-x-auto hide-scrollbar pl-4" style="gap: 10px">
          <div v-for="(amount, name) in statsByStudio" :key="name" class="studio-pill flex-shrink-0">
            <div class="studio-pill-name">{{ name }}</div>
            <div class="studio-pill-amount">{{ amount.toFixed(2) }}</div>
            <div class="studio-pill-bar">
              <div class="studio-pill-bar-fill" :style="{ width: (amount / grandTotal) * 100 + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- ============ CONTROL BAR ============ -->
    <v-card flat width="100%" class="mb-4 rounded-xl control-bar" dark>
      <div class="d-flex align-center pa-3">
        <v-btn v-if="$vuetify.breakpoint.smAndDown && canViewAll" icon @click="statsDialog = true" color="primary" class="mr-2">
          <v-icon>mdi-chart-donut</v-icon>
        </v-btn>

        <v-btn-toggle
          v-if="$vuetify.breakpoint.mdAndUp"
          v-model="viewMode"
          mandatory
          rounded
          class="mr-3 view-toggle"
          background-color="transparent"
          borderless
          dense>
          <v-btn value="grid" small><v-icon small>mdi-view-grid-outline</v-icon></v-btn>
          <v-btn value="table" small><v-icon small>mdi-table</v-icon></v-btn>
        </v-btn-toggle>

        <v-text-field
          v-model="filters.search"
          placeholder="Suche Shop, Betrag, Item..."
          prepend-inner-icon="mdi-magnify"
          solo
          flat
          dense
          hide-details
          rounded
          background-color="rgba(255,255,255,0.04)"
          class="flex-grow-1 search-field"
          style="max-width: 420px"
          color="primary"
          clearable
          @click:clear="onSearchInput"
          @input="onSearchInput"></v-text-field>

        <v-btn icon @click="showFilters = !showFilters" class="d-md-none ml-2" :color="showFilters ? 'primary' : ''">
          <v-icon>{{ showFilters ? "mdi-chevron-up" : "mdi-tune-variant" }}</v-icon>
        </v-btn>

        <div v-if="$vuetify.breakpoint.mdAndUp" class="d-flex align-center ml-2">
          <v-select
            v-model="filters.studioIds"
            :items="filterStudios"
            item-text="name"
            item-value="_id"
            placeholder="Alle Studios"
            solo
            flat
            dense
            hide-details
            rounded
            multiple
            small-chips
            deletable-chips
            background-color="rgba(255,255,255,0.04)"
            style="width: 220px"
            class="mx-2"
            @change="loadData"></v-select>

          <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y min-width="auto" dark>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" rounded class="date-btn" depressed>
                <v-icon left small>mdi-calendar-month-outline</v-icon>
                {{ dateDisplay }}
              </v-btn>
            </template>
            <v-card color="#222" class="rounded-lg">
              <v-date-picker
                v-model="filters.month"
                type="month"
                no-title
                scrollable
                locale="de-DE"
                color="primary"
                @input="
                  dateMenu = false
                  loadData()
                "
                dark></v-date-picker>
            </v-card>
          </v-menu>
          <!-- Visierungs-Filter -->
          <v-btn-toggle
            v-model="signedFilter"
            mandatory
            rounded
            dense
            @change="loadData"
            class="mx-2 view-toggle"
            background-color="transparent"
            borderless>
            <v-btn value="all" small>Alle</v-btn>
            <v-btn value="unsigned" small>
              <v-icon small>mdi-clock-outline</v-icon>
            </v-btn>
            <v-btn value="signed" small>
              <v-icon small>mdi-check-decagram</v-icon>
            </v-btn>
          </v-btn-toggle>

          <!-- Selection Mode Toggle -->
          <v-btn
            v-if="canViewAll"
            icon
            @click="
              selectionMode = !selectionMode
              selectedIds = []
            "
            :color="selectionMode ? 'primary' : ''"
            class="ml-1">
            <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
          </v-btn>

          <!-- Download All as ZIP -->
          <v-btn v-if="canViewAll" icon @click="downloadAllCurrent" :loading="downloadingBulk" class="ml-1" color="grey lighten-1">
            <v-icon>mdi-folder-zip-outline</v-icon>
          </v-btn>
          <v-btn v-if="canViewAll" icon color="grey lighten-1" @click="downloadExport" :loading="loadingExport" class="ml-1">
            <v-icon>mdi-file-pdf-box</v-icon>
          </v-btn>

          <v-btn color="primary" rounded depressed @click="openUploadDialog" class="ml-2 px-5 upload-btn">
            <v-icon left>mdi-plus</v-icon> Upload
          </v-btn>
        </div>
      </div>

      <!-- Mobile Filter Expand -->
      <v-expand-transition>
        <div v-if="showFilters" class="d-md-none px-3 pb-3">
          <v-divider class="mb-3" style="opacity: 0.1"></v-divider>
          <v-row dense>
            <v-col cols="12">
              <v-select
                v-model="filters.studioIds"
                :items="filterStudios"
                item-text="name"
                item-value="_id"
                label="Studios filtern"
                outlined
                dense
                hide-details
                rounded
                multiple
                small-chips
                deletable-chips
                @change="loadData"></v-select>
            </v-col>
            <v-col cols="12">
              <v-menu v-model="mobileDateMenu" :close-on-content-click="false" offset-y min-width="auto" dark>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn block outlined rounded class="justify-start" v-bind="attrs" v-on="on">
                    <v-icon left>mdi-calendar</v-icon> {{ dateDisplay }}
                  </v-btn>
                </template>
                <v-card color="#222">
                  <v-date-picker
                    v-model="filters.month"
                    type="month"
                    no-title
                    scrollable
                    locale="de-DE"
                    color="primary"
                    @input="
                      mobileDateMenu = false
                      loadData()
                    "
                    dark></v-date-picker>
                </v-card>
              </v-menu>
            </v-col>
            <v-col cols="12">
              <div class="caption muted-text mb-2 text-uppercase letter-spaced">Status</div>
              <v-btn-toggle v-model="signedFilter" mandatory rounded dense @change="loadData" class="w-100">
                <v-btn value="all" small style="flex: 1">Alle</v-btn>
                <v-btn value="unsigned" small style="flex: 1"> <v-icon small left>mdi-clock-outline</v-icon>Offen </v-btn>
                <v-btn value="signed" small style="flex: 1"> <v-icon small left>mdi-check-decagram</v-icon>Visiert </v-btn>
              </v-btn-toggle>
            </v-col>

            <v-col cols="6" v-if="canViewAll">
              <v-btn
                block
                outlined
                rounded
                @click="
                  selectionMode = !selectionMode
                  selectedIds = []
                "
                :color="selectionMode ? 'primary' : ''"
                height="40">
                <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon>
                {{ selectionMode ? "Auswahl AUS" : "Auswählen" }}
              </v-btn>
            </v-col>

            <v-col cols="6" v-if="canViewAll">
              <v-btn block outlined rounded @click="downloadAllCurrent" :loading="downloadingBulk" height="40">
                <v-icon left small>mdi-folder-zip-outline</v-icon>ZIP
              </v-btn>
            </v-col>
            <v-col cols="6" v-if="canViewAll">
              <v-btn block outlined rounded @click="downloadExport" :loading="loadingExport" height="40">
                <v-icon left small>mdi-file-pdf-box</v-icon> Export
              </v-btn>
            </v-col>
            <v-col :cols="canViewAll ? 6 : 12">
              <v-btn block color="primary" rounded depressed @click="openUploadDialog" height="40"> <v-icon left>mdi-plus</v-icon> Upload </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
    </v-card>
    <v-slide-y-transition>
      <v-card v-if="selectionMode && selectedIds.length > 0" flat dark class="mb-2 rounded-xl px-4 py-2 d-flex align-center selection-bar w-100">
        <v-checkbox :input-value="allSelected" @change="toggleSelectAll" hide-details dense dark class="mt-0 pt-0"></v-checkbox>
        <span class="ml-2 white--text">{{ selectedIds.length }} ausgewählt</span>
        <v-spacer></v-spacer>
        <v-btn v-if="canSign" text small color="success" @click="signSelected" :loading="signingBulk">
          <v-icon left small>mdi-check-decagram</v-icon>Visieren
        </v-btn>
        <v-btn text small color="primary" @click="downloadSelected" :loading="downloadingBulk"> <v-icon left small>mdi-download</v-icon>ZIP </v-btn>
      </v-card>
    </v-slide-y-transition>
    <!-- ============ LOADING SKELETONS ============ -->
    <div v-if="loading" class="w-100">
      <v-row class="ma-0">
        <v-col v-for="n in 8" :key="n" cols="12" sm="6" md="4" lg="3" xl="2" class="pa-2">
          <v-card flat dark color="#1a1a1a" class="rounded-xl skeleton-card">
            <div class="skeleton-img shimmer"></div>
            <div class="pa-3">
              <div class="skeleton-line shimmer mb-2" style="width: 40%"></div>
              <div class="skeleton-line shimmer mb-2" style="width: 70%; height: 16px"></div>
              <div class="skeleton-line shimmer" style="width: 50%; height: 22px"></div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- ============ CONTENT ============ -->
    <div style="background-color: #1e1e1e" v-else class="w-100">
      <!-- EMPTY STATE -->
      <div v-if="bills.length === 0" class="empty-state text-center py-12">
        <div class="empty-icon-wrap">
          <v-icon size="72" color="grey darken-2">mdi-receipt-text-outline</v-icon>
        </div>
        <div class="text-h6 grey--text text--lighten-1 mt-4">Keine Belege gefunden</div>
        <div class="caption muted-text mt-1">Versuche andere Filter oder lade deinen ersten Beleg hoch</div>
        <v-btn rounded color="primary" depressed class="mt-5 px-6" @click="openUploadDialog"> <v-icon left>mdi-plus</v-icon> Beleg hochladen </v-btn>
      </div>

      <!-- GRID VIEW (Desktop) -->
      <v-row v-else-if="viewMode === 'grid' && $vuetify.breakpoint.mdAndUp" class="ma-0">
        <v-col
          v-for="(bill, idx) in bills"
          :key="bill._id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
          class="pa-2 stagger-item"
          :style="{ animationDelay: Math.min(idx * 30, 600) + 'ms' }">
          <v-card dark flat class="rounded-xl fill-height d-flex flex-column receipt-card" @click="openPreview(bill)">
            <div class="receipt-img-wrap" v-intersect="(e) => onCardVisible(e, bill)">
              <v-img v-if="bill.previewUrl" :src="bill.previewUrl" height="170" class="receipt-img" eager>
                <template v-slot:placeholder>
                  <div class="fill-height d-flex align-center justify-center">
                    <v-progress-circular indeterminate color="grey darken-1" size="24" width="2"></v-progress-circular>
                  </div>
                </template>
              </v-img>
              <div v-else class="receipt-img-placeholder">
                <v-icon size="48" color="grey darken-2">mdi-image-outline</v-icon>
              </div>
              <div v-if="selectionMode" class="receipt-checkbox" @click.stop="toggleSelection(bill._id)">
                <v-icon :color="selectedIds.includes(bill._id) ? 'primary' : 'grey'">
                  {{ selectedIds.includes(bill._id) ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
                </v-icon>
              </div>

              <!-- NEU: Signed Badge -->
              <div v-if="bill.signedBy" class="receipt-signed-badge" :title="'Visiert von ' + bill.signedBy.vorname">
                <v-icon x-small color="success">mdi-check-decagram</v-icon>
              </div>
              <div class="receipt-img-overlay">
                <div class="d-flex justify-space-between align-end">
                  <span class="caption white--text font-weight-medium">{{ formatDate(bill.date) }}</span>
                  <v-icon v-if="isToday(bill.createdAt || bill.date)" small color="success" class="pulse-dot"> mdi-circle </v-icon>
                </div>
              </div>
              <div class="receipt-studio-badge">
                {{ bill.studio ? bill.studio.name : "N/A" }}
              </div>
            </div>

            <v-card-text class="flex-grow-1 pa-3">
              <div class="text-subtitle-2 white--text font-weight-bold text-truncate">
                {{ bill.storeName || "Unbekannt" }}
              </div>
              <div class="amount-line mt-1">
                <span class="currency-mini">CHF</span>
                <span class="amount-mini">{{ bill.amount ? parseFloat(bill.amount).toFixed(2) : "0.00" }}</span>
              </div>
              <div class="d-flex align-center mt-2">
                <v-avatar size="20" class="uploader-avatar mr-2">
                  <span class="caption font-weight-bold">{{ getInitials(bill.uploader) }}</span>
                </v-avatar>
                <div class="caption muted-text text-truncate">{{ bill.uploader.vorname }}</div>
                <v-spacer></v-spacer>
                <!-- NEU: Kommentar-Indikator -->
                <span v-if="bill.comments && bill.comments.length" class="caption muted-text mr-2">
                  <v-icon x-small color="primary">mdi-comment-text</v-icon>
                  {{ bill.comments.length }}
                </span>

                <span v-if="bill.lineItems && bill.lineItems.length" class="caption muted-text">
                  <v-icon x-small color="grey">mdi-format-list-bulleted</v-icon>
                  {{ bill.lineItems.length }}
                </span>
              </div>
            </v-card-text>

            <div class="receipt-actions">
              <v-btn icon x-small @click.stop="openEditDialog(bill)">
                <v-icon small color="grey lighten-1">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon x-small @click.stop="downloadFile(bill)">
                <v-icon small color="grey lighten-1">mdi-download</v-icon>
              </v-btn>
              <!-- NEU: Kommentar -->
              <v-btn icon x-small @click.stop="openCommentDialog(bill)">
                <v-icon small :color="bill.comments && bill.comments.length ? 'primary' : 'grey lighten-1'"> mdi-comment-outline </v-icon>
              </v-btn>

              <!-- NEU: Visieren (nur für Steffen) -->
              <v-btn v-if="canSign" icon x-small @click.stop="toggleSign(bill)">
                <v-icon small :color="bill.signedBy ? 'success' : 'grey lighten-1'">
                  {{ bill.signedBy ? "mdi-check-decagram" : "mdi-check-decagram-outline" }}
                </v-icon>
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn icon x-small @click.stop="confirmDelete(bill)">
                <v-icon small color="red lighten-1">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- MOBILE LIST -->
      <div v-else-if="$vuetify.breakpoint.smAndDown">
        <v-card
          v-for="(bill, idx) in bills"
          :key="bill._id"
          flat
          dark
          class="mobile-row-card mb-2 rounded-xl stagger-item"
          :style="{ animationDelay: Math.min(idx * 25, 500) + 'ms' }"
          v-intersect="(e) => onCardVisible(e, bill)"
          v-ripple>
          <div class="d-flex align-center pa-3">
            <!-- NEU: Checkbox im Selection Mode -->
            <v-icon
              v-if="selectionMode"
              :color="selectedIds.includes(bill._id) ? 'primary' : 'grey'"
              class="mr-2"
              @click.stop="toggleSelection(bill._id)">
              {{ selectedIds.includes(bill._id) ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
            </v-icon>

            <v-avatar
              tile
              size="52"
              class="rounded-lg mr-3 mobile-thumb"
              @click.stop="selectionMode ? toggleSelection(bill._id) : openEditDialog(bill)">
              <v-img v-if="bill.previewUrl" :src="bill.previewUrl"></v-img>
              <v-icon v-else color="grey darken-1">mdi-image-outline</v-icon>
            </v-avatar>

            <div class="flex-grow-1 overflow-hidden mr-2" @click="selectionMode ? toggleSelection(bill._id) : openEditDialog(bill)">
              <div class="white--text font-weight-bold text-truncate">
                {{ bill.storeName || "Unbekannt" }}
              </div>
              <div class="caption muted-text d-flex align-center mt-1">
                <v-icon x-small class="mr-1">mdi-calendar-blank-outline</v-icon>
                {{ formatDate(bill.date) }}
                <span class="mx-1">·</span>
                {{ bill.studio ? bill.studio.name : "—" }}
                <v-icon v-if="bill.signedBy" x-small color="success" class="ml-2">mdi-check-decagram</v-icon>
                <span v-if="bill.comments && bill.comments.length" class="ml-2 d-flex align-center">
                  <v-icon x-small color="primary">mdi-comment-text</v-icon>
                  <span class="ml-1">{{ bill.comments.length }}</span>
                </span>
              </div>
            </div>

            <div class="text-right mr-1" @click="selectionMode ? toggleSelection(bill._id) : openEditDialog(bill)">
              <div class="mobile-amount">{{ bill.amount ? parseFloat(bill.amount).toFixed(2) : "0.00" }}</div>
              <div class="caption muted-text">CHF</div>
            </div>

            <!-- NEU: Dreipunkt-Button für Actions -->
            <v-btn icon small @click.stop="openMobileActions(bill)">
              <v-icon color="grey lighten-1">mdi-dots-vertical</v-icon>
            </v-btn>
          </div>
        </v-card>
      </div>

      <!-- TABLE VIEW -->
      <v-card v-else flat dark color="#1a1a1a" class="rounded-xl overflow-hidden">
        <v-data-table
          :headers="tableHeaders"
          :items="bills"
          :items-per-page="50"
          class="cc-table"
          @click:row="openPreview"
          :expanded.sync="expanded"
          item-key="_id"
          show-expand
          :mobile-breakpoint="0">
          <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
          <template v-slot:item.uploader="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="22" class="uploader-avatar mr-2">
                <span class="caption font-weight-bold">{{ getInitials(item.uploader) }}</span>
              </v-avatar>
              {{ item.uploader.vorname }}
            </div>
          </template>
          <template v-slot:item.amount="{ item }">
            <span class="font-weight-bold primary--text">CHF {{ item.amount.toFixed(2) }}</span>
          </template>
          <template v-slot:item.select="{ item }">
            <v-checkbox
              v-if="selectionMode"
              :input-value="selectedIds.includes(item._id)"
              @click.stop="toggleSelection(item._id)"
              hide-details
              dense></v-checkbox>
          </template>

          <template v-slot:item.signed="{ item }">
            <v-tooltip v-if="item.signedBy" bottom>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" color="success">mdi-check-decagram</v-icon>
              </template>
              <span>{{ item.signedBy.vorname }} {{ item.signedBy.nachname }}<br />{{ formatDateTime(item.signedAt) }}</span>
            </v-tooltip>
            <v-icon v-else color="grey darken-2">mdi-clock-outline</v-icon>
          </template>

          <template v-slot:item.comments="{ item }">
            <v-badge v-if="item.comments && item.comments.length" :content="item.comments.length" color="primary" inline>
              <v-icon small>mdi-comment-text</v-icon>
            </v-badge>
            <span v-else class="caption muted-text">—</span>
          </template>
          <template v-slot:item.studio="{ item }">
            <v-chip x-small class="studio-chip">{{ item.studio ? item.studio.name : "N/A" }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click.stop="openEditDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small @click.stop="confirmDelete(item)">
              <v-icon small color="red lighten-1">mdi-delete-outline</v-icon>
            </v-btn>
            <v-btn icon small @click.stop="downloadFile(item)">
              <v-icon small>mdi-download</v-icon>
            </v-btn>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-card flat dark color="#141414" class="pa-4 rounded-0">
                <div class="caption muted-text mb-2 text-uppercase letter-spaced">
                  <v-icon small left>mdi-format-list-bulleted</v-icon>
                  Positionen ({{ item.lineItems ? item.lineItems.length : 0 }})
                </div>
                <div v-if="item.lineItems && item.lineItems.length > 0">
                  <v-row v-for="(line, i) in item.lineItems" :key="i" dense class="caption py-1 line-item-row">
                    <v-col cols="8" class="text-truncate">
                      <span class="font-weight-bold mr-2 muted-text">{{ line.quantity || 1 }}×</span>
                      <span
                        :class="{ 'highlight-match': filters.search && line.name && line.name.toLowerCase().includes(filters.search.toLowerCase()) }">
                        {{ line.name }}
                      </span>
                    </v-col>
                    <v-col cols="4" class="text-right">
                      {{ line.price ? parseFloat(line.price).toFixed(2) : "0.00" }}
                    </v-col>
                  </v-row>
                </div>
                <div v-else class="caption muted-text font-italic">Keine Positionen erfasst.</div>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- ============ FAB ============ -->
    <v-btn fab fixed bottom right large color="primary" elevation="12" class="d-md-none mb-4 mr-2 fab-btn" @click="openUploadDialog">
      <v-icon>mdi-camera-plus</v-icon>
    </v-btn>

    <!-- ============ MOBILE ACTION SHEET ============ -->
    <v-bottom-sheet v-model="mobileActionSheet" dark>
      <v-card color="#1a1a1a" v-if="mobileActionBill">
        <v-card-title class="font-weight-bold">
          <v-icon left>mdi-receipt-text-outline</v-icon>
          {{ mobileActionBill.storeName || "Beleg" }}
          <v-spacer></v-spacer>
          <span class="primary--text">CHF {{ mobileActionBill.amount ? mobileActionBill.amount.toFixed(2) : "0.00" }}</span>
        </v-card-title>
        <v-divider style="opacity: 0.1"></v-divider>
        <v-list color="#1a1a1a" dark>
          <v-list-item @click="closeMobileActionsAnd(() => openEditDialog(mobileActionBill))">
            <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
            <v-list-item-title>Bearbeiten</v-list-item-title>
          </v-list-item>

          <v-list-item @click="closeMobileActionsAnd(() => openPreview(mobileActionBill))">
            <v-list-item-icon><v-icon>mdi-eye-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Beleg anzeigen</v-list-item-title>
          </v-list-item>

          <v-list-item @click="closeMobileActionsAnd(() => openCommentDialog(mobileActionBill))">
            <v-list-item-icon>
              <v-icon :color="mobileActionBill.comments && mobileActionBill.comments.length ? 'primary' : ''"> mdi-comment-outline </v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              Kommentare
              <span v-if="mobileActionBill.comments && mobileActionBill.comments.length" class="caption muted-text ml-1">
                ({{ mobileActionBill.comments.length }})
              </span>
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="canSign" @click="closeMobileActionsAnd(() => toggleSign(mobileActionBill))">
            <v-list-item-icon>
              <v-icon :color="mobileActionBill.signedBy ? 'success' : ''">
                {{ mobileActionBill.signedBy ? "mdi-check-decagram" : "mdi-check-decagram-outline" }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              {{ mobileActionBill.signedBy ? "Visierung aufheben" : "Visieren" }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="mobileActionBill.signedBy" class="caption muted-text">
              von {{ mobileActionBill.signedBy.vorname }} {{ formatDateTime(mobileActionBill.signedAt) }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item @click="closeMobileActionsAnd(() => downloadFile(mobileActionBill))">
            <v-list-item-icon><v-icon>mdi-download</v-icon></v-list-item-icon>
            <v-list-item-title>Herunterladen</v-list-item-title>
          </v-list-item>

          <v-divider style="opacity: 0.1"></v-divider>

          <v-list-item @click="closeMobileActionsAnd(() => confirmDelete(mobileActionBill))">
            <v-list-item-icon><v-icon color="red lighten-1">mdi-delete-outline</v-icon></v-list-item-icon>
            <v-list-item-title class="red--text text--lighten-1">Löschen</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-bottom-sheet>

    <!-- ============ STATS DIALOG (Mobile) ============ -->
    <v-dialog v-model="statsDialog" max-width="420" dark content-class="rounded-xl">
      <v-card color="#1a1a1a" class="rounded-xl">
        <v-card-title class="font-weight-bold">Statistik</v-card-title>
        <v-card-text>
          <div class="text-center mb-4 mt-2">
            <div class="caption muted-text text-uppercase letter-spaced">Gesamt</div>
            <div class="display-1 font-weight-black primary--text mt-1">CHF {{ grandTotal.toFixed(2) }}</div>
          </div>
          <v-divider class="mb-4" style="opacity: 0.1"></v-divider>
          <div v-for="(amount, name) in statsByStudio" :key="name" class="studio-pill mb-3" style="min-width: 0">
            <div class="d-flex justify-space-between align-center">
              <span class="studio-pill-name">{{ name }}</span>
              <span class="studio-pill-amount">CHF {{ amount.toFixed(2) }}</span>
            </div>
            <div class="studio-pill-bar mt-2">
              <div class="studio-pill-bar-fill" :style="{ width: (amount / grandTotal) * 100 + '%' }"></div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="statsDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ DELETE DIALOG ============ -->
    <v-dialog v-model="deleteDialog" max-width="450" dark>
      <v-card color="#1a1a1a" class="rounded-xl">
        <v-card-title class="font-weight-bold"> <v-icon color="red lighten-1" left>mdi-alert-circle-outline</v-icon> Beleg löschen? </v-card-title>
        <v-card-text class="grey--text text--lighten-1">
          Möchtest du den Beleg von <b>{{ billToDelete?.storeName }}</b> ({{ billToDelete ? parseFloat(billToDelete.amount).toFixed(2) : "" }} CHF)
          wirklich unwiderruflich löschen?
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn color="red darken-1" rounded depressed @click="executeDelete" :loading="deleting">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ UPLOAD/EDIT DIALOG ============ -->
    <!-- (unverändert in Logik – nur Stil-Klassen passen sich automatisch an) -->
    <v-dialog v-model="dialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="950" persistent dark transition="dialog-bottom-transition">
      <v-card class="d-flex flex-column" color="#0f0f0f">
        <v-toolbar color="#1a1a1a" elevation="0">
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title class="font-weight-bold">
            {{ isEditMode ? "Beleg Bearbeiten" : aiStep === 1 ? "Neuer Beleg" : aiStep === 2 ? "AI Analyse" : "Prüfung" }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>

        <v-card-text class="flex-grow-1 pa-0 d-flex flex-column" style="position: relative; overflow-y: auto">
          <!-- STEP 1 UPLOAD -->
          <div v-if="aiStep === 1" class="d-flex flex-column align-center justify-center fill-height pa-6">
            <div class="upload-zone d-flex flex-column align-center justify-center w-100 rounded-xl" @click="triggerUploadFlow">
              <div class="circle-bg mb-6">
                <v-icon size="48" color="primary">mdi-camera-outline</v-icon>
              </div>
              <div class="text-h6 mb-2 text-center">
                {{ $vuetify.breakpoint.xsOnly ? "Foto machen" : "Datei auswählen" }}
              </div>
              <div class="caption muted-text">JPG, PNG oder PDF</div>
              <input ref="fileInput" type="file" class="d-none" accept="image/*,application/pdf" @change="(e) => onFileSelected(e.target.files[0])" />
            </div>
          </div>

          <!-- STEP 2 AI -->
          <div v-if="aiStep === 2" class="d-flex flex-column align-center justify-center fill-height pa-6 w-100">
            <div class="scanner-container mb-6">
              <div class="scanner-line"></div>
              <v-icon size="80" color="primary">mdi-brain</v-icon>
            </div>
            <v-card
              width="100%"
              max-width="600"
              color="#0a0a0a"
              class="rounded-lg elevation-6 d-flex flex-column terminal-card"
              style="height: 300px">
              <div class="d-flex align-center px-3 py-1" style="border-bottom: 1px solid #222">
                <div class="d-flex mr-2">
                  <div class="rounded-circle red mr-1" style="width: 10px; height: 10px"></div>
                  <div class="rounded-circle yellow mr-1" style="width: 10px; height: 10px"></div>
                  <div class="rounded-circle green" style="width: 10px; height: 10px"></div>
                </div>
                <span class="caption muted-text font-family-mono ml-2">ai_vision.exe</span>
              </div>
              <div
                ref="terminalOutput"
                class="pa-4 font-family-mono caption white--text overflow-y-auto flex-grow-1"
                style="white-space: pre-wrap; word-break: break-word; line-height: 1.4">
                <span style="color: #4caf50">{{ reasoningText }}</span
                ><span class="cursor-blink">_</span>
              </div>
            </v-card>
            <div class="caption muted-text mt-4">AI analysiert den Beleg...</div>
          </div>

          <!-- STEP 3 FORM -->
          <div v-if="aiStep === 3" class="fill-height d-flex flex-column">
            <v-tabs v-if="$vuetify.breakpoint.xsOnly" v-model="mobileTab" background-color="#1a1a1a" grow dark centered icons-and-text height="60">
              <v-tab><v-icon class="mb-1">mdi-file-document-edit-outline</v-icon>Daten</v-tab>
              <v-tab><v-icon class="mb-1">mdi-image-outline</v-icon>Beleg</v-tab>
            </v-tabs>
            <div class="d-flex flex-grow-1 flex-column flex-md-row" style="overflow: hidden">
              <div
                class="grey darken-4 d-flex align-center justify-center"
                :class="{ 'd-none': $vuetify.breakpoint.xsOnly && mobileTab !== 1, 'col-md-5': true }"
                style="position: relative; overflow: hidden; height: 100%">
                <v-img v-if="currentUploadPreview" :src="currentUploadPreview" contain max-height="100%"></v-img>
                <div v-else class="caption muted-text">Keine Vorschau</div>
              </div>
              <div
                class="pa-4 pa-md-6"
                :class="{ 'd-none': $vuetify.breakpoint.xsOnly && mobileTab !== 0, 'col-md-7': true }"
                style="overflow-y: auto; height: 100%">
                <v-form ref="form" v-model="valid">
                  <v-card color="#1a1a1a" class="pa-4 rounded-xl mb-4" flat>
                    <div class="caption muted-text text-uppercase mb-1 text-center letter-spaced">Betrag (CHF)</div>
                    <v-text-field
                      v-model="newBill.amount"
                      type="number"
                      placeholder="0.00"
                      solo
                      flat
                      background-color="transparent"
                      class="custom-amount-input"
                      hide-details
                      height="60"
                      :rules="[(v) => !!v || 'Betrag fehlt']"></v-text-field>
                  </v-card>
                  <v-row dense>
                    <v-col cols="12">
                      <v-text-field
                        v-model="newBill.storeName"
                        label="Händler / Geschäft"
                        outlined
                        background-color="#1a1a1a"
                        prepend-inner-icon="mdi-store"></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-menu v-model="dateMenuUpload" offset-y min-width="auto" dark>
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatDateInput(newBill.date)"
                            label="Datum"
                            :prepend-inner-icon="aiDetectedDate && !dateConfirmed ? 'mdi-robot-outline' : 'mdi-calendar'"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            :background-color="aiDetectedDate && !dateConfirmed ? '#3d2e00' : '#1a1a1a'"
                            :color="aiDetectedDate && !dateConfirmed ? 'warning' : 'primary'"
                            :hint="aiDetectedDate && !dateConfirmed ? '⚠️ Von AI erkannt – bitte prüfen!' : ''"
                            :persistent-hint="aiDetectedDate && !dateConfirmed"
                            :rules="[(v) => !!v || 'Pflichtfeld']"></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="newBill.date"
                          no-title
                          locale="de-DE"
                          color="primary"
                          first-day-of-week="1"
                          @input="onDateConfirmed"
                          dark></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="newBill.tax" label="MWST (CHF)" type="number" outlined background-color="#1a1a1a"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-select
                        v-model="newBill.studio"
                        :items="studios"
                        item-text="name"
                        item-value="_id"
                        label="Studio Zuordnung"
                        outlined
                        background-color="#1a1a1a"
                        prepend-inner-icon="mdi-map-marker"
                        :rules="[(v) => !!v || 'Bitte wählen']"></v-select>
                    </v-col>
                  </v-row>
                  <div class="d-flex justify-space-between align-center mt-4 mb-2">
                    <span class="subtitle-2 muted-text">Positionen</span>
                    <v-btn text small color="primary" @click="addLineItem"><v-icon left>mdi-plus</v-icon>Add</v-btn>
                  </div>
                  <v-slide-y-transition group>
                    <div
                      v-for="(item, index) in newBill.lineItems"
                      :key="index"
                      class="d-flex align-center mb-2 pa-2 rounded-lg"
                      style="background: #1a1a1a">
                      <v-text-field
                        v-model.number="item.quantity"
                        type="number"
                        placeholder="1"
                        hide-details
                        dense
                        flat
                        solo
                        background-color="transparent"
                        class="centered-input"
                        style="max-width: 60px"></v-text-field>
                      <div class="muted-text mx-1 caption">×</div>
                      <v-text-field
                        v-model="item.name"
                        placeholder="Artikel"
                        hide-details
                        dense
                        flat
                        solo
                        background-color="transparent"
                        style="flex: 3"></v-text-field>
                      <v-divider vertical class="mx-2"></v-divider>
                      <v-text-field
                        v-model.number="item.price"
                        placeholder="0.00"
                        hide-details
                        dense
                        flat
                        solo
                        type="number"
                        background-color="transparent"
                        style="flex: 1"
                        class="text-right"></v-text-field>
                      <v-btn icon x-small color="red lighten-2" class="ml-2" @click="removeLineItem(index)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-slide-y-transition>
                </v-form>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider v-if="aiStep === 3" style="opacity: 0.1"></v-divider>
        <v-card-actions v-if="aiStep === 3" class="pa-4">
          <v-btn v-if="!isEditMode" text color="grey" @click="aiStep = 1" :disabled="uploading">Neu Scannen</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            x-large
            color="primary"
            class="px-8 font-weight-bold upload-btn"
            rounded
            depressed
            @click="saveBill"
            :loading="uploading"
            :disabled="!valid">
            <v-icon left>mdi-check</v-icon> {{ isEditMode ? "Aktualisieren" : "Speichern" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ PREVIEW (mit Zoom) ============ -->
    <v-dialog v-model="previewDialog" fullscreen hide-overlay transition="fade-transition" dark>
      <v-card color="black" class="d-flex flex-column preview-card">
        <!-- Toolbar -->
        <div class="preview-toolbar">
          <v-btn icon @click="resetZoom" :disabled="zoomScale === 1" class="mr-1">
            <v-icon>mdi-magnify-minus-outline</v-icon>
          </v-btn>
          <v-btn icon @click="zoomIn" class="mr-1">
            <v-icon>mdi-magnify-plus-outline</v-icon>
          </v-btn>
          <span class="caption muted-text ml-2">{{ Math.round(zoomScale * 100) }}%</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closePreview" style="background: rgba(0, 0, 0, 0.6)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Zoom Container -->
        <div
          ref="zoomContainer"
          class="zoom-container"
          @wheel.prevent="onWheel"
          @touchstart.prevent="onTouchStart"
          @touchmove.prevent="onTouchMove"
          @touchend.prevent="onTouchEnd"
          @mousedown="onMouseDown"
          @dblclick="onDoubleClick">
          <img ref="zoomImage" :src="currentPreviewUrl" class="zoom-image" :style="imageStyle" draggable="false" @load="onImageLoad" />
        </div>
      </v-card>
    </v-dialog>
    <!-- ============ COMMENT DIALOG ============ -->
    <v-dialog v-model="commentDialog" max-width="560" dark>
      <v-card color="#1a1a1a" class="rounded-xl" v-if="commentBill">
        <v-card-title class="font-weight-bold">
          <v-icon left>mdi-comment-text-multiple</v-icon>
          Kommentare
          <span class="ml-2 muted-text caption">{{ commentBill.storeName }}</span>
        </v-card-title>
        <v-card-text>
          <div v-if="!commentBill.comments || commentBill.comments.length === 0" class="text-center py-6 muted-text caption">
            Noch keine Kommentare
          </div>
          <div v-else class="mb-4" style="max-height: 300px; overflow-y: auto">
            <div v-for="c in commentBill.comments" :key="c._id" class="comment-item pa-3 mb-2 rounded-lg">
              <div class="d-flex align-center mb-1">
                <v-avatar size="24" class="uploader-avatar mr-2">
                  <span class="caption font-weight-bold">{{ getInitials(c.author) }}</span>
                </v-avatar>
                <span class="caption font-weight-bold white--text"> {{ c.author.vorname }} {{ c.author.nachname }} </span>
                <v-spacer></v-spacer>
                <span class="caption muted-text">{{ formatDateTime(c.createdAt) }}</span>
                <v-btn v-if="canDeleteComment(c)" icon x-small class="ml-1" @click="deleteComment(c._id)">
                  <v-icon x-small color="red lighten-2">mdi-close</v-icon>
                </v-btn>
              </div>
              <div class="body-2 white--text">{{ c.text }}</div>
            </div>
          </div>
          <v-textarea
            v-model="newCommentText"
            placeholder="Kommentar schreiben..."
            outlined
            rows="2"
            auto-grow
            hide-details
            background-color="#0f0f0f"
            dark></v-textarea>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="commentDialog = false">Schließen</v-btn>
          <v-btn color="primary" rounded depressed @click="submitComment" :disabled="!newCommentText.trim()" :loading="submittingComment">
            <v-icon left>mdi-send</v-icon>Senden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import moment from "moment"
import userService from "@/services/userService"

export default {
  data() {
    return {
      userService,
      bills: [],
      studios: [],
      loading: false,
      loadingExport: false,
      uploading: false,
      showFilters: false,
      reasoningText: "",
      viewMode: "grid",
      statsDialog: false,
      expanded: [],
      deleteDialog: false,
      billToDelete: null,
      deleting: false,
      isEditMode: false,
      editingId: null,
      dialog: false,
      previewDialog: false,
      aiStep: 1,
      uploadFile: null,
      currentUploadPreview: null,
      currentPreviewUrl: null,
      valid: false,
      dateMenuUpload: false,
      mobileTab: 0,
      animatedTotal: "0.00",
      _animFrame: null,
      _searchTimer: null,
      aiDetectedDate: false,
      dateConfirmed: false,

      dateMenu: false,
      filters: {
        month: moment().format("YYYY-MM"),
        range: [],
        search: "",
        studioIds: [],
      },
      newBill: { storeName: "", date: moment().format("YYYY-MM-DD"), studio: null, amount: null, tax: null, lineItems: [] },
      mobileDateMenu: false,
      stats: {
        grandTotal: 0,
        totalCount: 0,
        average: 0,
        byStudio: [],
      },
      selectedIds: [], // Multi-Select IDs
      selectionMode: false, // Auswahl-Modus aktiv
      signedFilter: "all", // 'all' | 'signed' | 'unsigned'
      commentDialog: false,
      commentBill: null,
      newCommentText: "",
      downloadingBulk: false,
      signingBulk: false,
      submittingComment: false,

      mobileActionSheet: false,
      mobileActionBill: null,

      // Zoom State
      zoomScale: 1,
      zoomTranslateX: 0,
      zoomTranslateY: 0,
      // Touch tracking
      _touchStartDist: 0,
      _touchStartScale: 1,
      _touchStartX: 0,
      _touchStartY: 0,
      _touchStartTranslateX: 0,
      _touchStartTranslateY: 0,
      _isPanning: false,
      _isPinching: false,
      // Mouse drag
      _mouseStartX: 0,
      _mouseStartY: 0,
      _isDragging: false,
    }
  },
  async created() {
    let studios = await api.fetchStudios()
    this.studios = studios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    this.loadData()
  },
  beforeDestroy() {
    // FIX: Memory Leak — alle Blob URLs freigeben
    this.revokeAllPreviewUrls()
    if (this._animFrame) cancelAnimationFrame(this._animFrame)
    if (this._searchTimer) clearTimeout(this._searchTimer)
  },
  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.zoomTranslateX}px, ${this.zoomTranslateY}px) scale(${this.zoomScale})`,
        cursor: this.zoomScale > 1 ? "grab" : "default",
        transition: this._isPanning || this._isPinching || this._isDragging ? "none" : "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      }
    },
    tableHeaders() {
      const headers = []
      if (this.selectionMode) {
        headers.push({ text: "", value: "select", sortable: false, width: "40px" })
      }
      headers.push(
        { text: "Datum", value: "date", width: "100px" },
        { text: "Händler", value: "storeName" },
        { text: "Uploader", value: "uploader" },
        { text: "Studio", value: "studio", width: "120px" },
        { text: "Betrag", value: "amount", align: "right" },
        { text: "Visiert", value: "signed", width: "90px", align: "center" },
        { text: "Komm.", value: "comments", width: "70px", align: "center" },
        { text: "", value: "actions", sortable: false, align: "right", width: "140px" },
      )
      return headers
    },
    canSign() {
      return userService.hasRight("cc_signBills")
    },
    allSelected() {
      return this.bills.length > 0 && this.selectedIds.length === this.bills.length
    },
    canViewAll() {
      return userService.hasRight("cc_viewAll")
    },
    grandTotal() {
      return this.stats.grandTotal || 0
    },
    averageAmount() {
      return (this.stats.average || 0).toFixed(2)
    },
    statsByStudio() {
      // Wandelt Array in Objekt für die bestehenden v-for Loops
      const obj = {}
      this.stats.byStudio.forEach((s) => {
        obj[s.name] = s.total
      })
      return obj
    },
    filterStudios() {
      return this.studios
    },
    dateDisplay() {
      return moment(this.filters.month).format("MMMM YYYY")
    },
  },
  watch: {
    grandTotal: {
      handler(val) {
        this.animateNumber(val)
      },
      immediate: true,
    },
  },
  methods: {
    async loadPdfJs() {
      if (window.pdfjsLib) return window.pdfjsLib

      return new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = "https://app.wellcomefit.ch/img/js/pdf.min.js"
        script.onload = () => {
          const lib = window["pdfjs-dist/build/pdf"]
          // Worker ebenfalls vom CDN setzen für maximale Kompatibilität
          lib.GlobalWorkerOptions.workerSrc = "https://app.wellcomefit.ch/img/js/pdf.worker.min.js"
          resolve(lib)
        }
        script.onerror = () => {
          this.$toast.error("PDF-Modul konnte nicht geladen werden.")
          reject()
        }
        document.head.appendChild(script)
      })
    },
    async convertPdfToImage(file, pdfjsLib) {
      const arrayBuffer = await file.arrayBuffer()
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
      const pdf = await loadingTask.promise

      const numPages = Math.min(pdf.numPages, 10) // Sicherheitshalber Max 10 Seiten
      const pages = []
      let totalHeight = 0
      let maxWidth = 0

      // 1. Metadaten aller Seiten sammeln
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i)
        const viewport = page.getViewport({ scale: 1.5 })
        pages.push({ page, viewport })
        totalHeight += viewport.height
        maxWidth = Math.max(maxWidth, viewport.width)
      }

      // 2. Canvas erstellen
      const canvas = document.createElement("canvas")
      const context = canvas.getContext("2d")
      canvas.width = maxWidth
      canvas.height = totalHeight

      // Hintergrund auf Weiss setzen (verhindert Transparenz-Fehler)
      context.fillStyle = "white"
      context.fillRect(0, 0, canvas.width, canvas.height)

      // 3. Jede Seite nacheinander zeichnen
      let currentY = 0
      for (const item of pages) {
        // Wir erstellen ein temporäres Canvas für die einzelne Seite
        // Das ist am sichersten gegen Überlappungsfehler
        const tempCanvas = document.createElement("canvas")
        const tempCtx = tempCanvas.getContext("2d")
        tempCanvas.width = item.viewport.width
        tempCanvas.height = item.viewport.height

        await item.page.render({
          canvasContext: tempCtx,
          viewport: item.viewport,
        }).promise

        // Jetzt die gerenderte Seite in das Haupt-Canvas kopieren
        context.drawImage(tempCanvas, 0, currentY)

        currentY += item.viewport.height
      }

      return canvas.toDataURL("image/jpeg", 0.8)
    },
    onDateConfirmed() {
      this.dateMenuUpload = false
      this.dateConfirmed = true // User hat aktiv das Datum angefasst/bestätigt
      this.aiDetectedDate = false
    },
    formatDateInput(d) {
      return d ? moment(d).format("DD.MM.YYYY") : ""
    },
    openMobileActions(bill) {
      if (this.selectionMode) {
        this.toggleSelection(bill._id)
        return
      }
      this.mobileActionBill = bill
      this.mobileActionSheet = true
    },
    closeMobileActionsAnd(callback) {
      this.mobileActionSheet = false
      // Kurz warten bis Sheet zu ist, damit die nächste Aktion sauber aufgeht
      setTimeout(() => {
        if (callback) callback()
      }, 200)
    },
    async signSelected() {
      if (this.selectedIds.length === 0) return
      this.signingBulk = true
      try {
        await api.creditCardBills.signBulk(this.selectedIds)
        this.$toast.success(`${this.selectedIds.length} Belege visiert`)
        this.selectedIds = []
        this.selectionMode = false
        this.loadData()
      } catch (e) {
        this.$toast.error("Fehler beim Visieren")
      } finally {
        this.signingBulk = false
      }
    },
    canDeleteComment(comment) {
      const currentUserId = userService.getUserId ? userService.getUserId() : null
      return comment.author._id === currentUserId || this.canViewAll
    },
    toggleSelection(billId) {
      const idx = this.selectedIds.indexOf(billId)
      if (idx === -1) this.selectedIds.push(billId)
      else this.selectedIds.splice(idx, 1)
    },
    toggleSelectAll() {
      if (this.allSelected) this.selectedIds = []
      else this.selectedIds = this.bills.map((b) => b._id)
    },
    async toggleSign(bill) {
      if (!this.canSign) return
      try {
        const updated = await api.creditCardBills.sign(bill._id)
        const idx = this.bills.findIndex((b) => b._id === bill._id)
        if (idx !== -1) {
          updated.previewUrl = this.bills[idx].previewUrl
          this.$set(this.bills, idx, updated)
        }
        this.$toast.success(updated.signedBy ? "Visiert" : "Visierung aufgehoben")
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    openCommentDialog(bill) {
      this.commentBill = bill
      this.newCommentText = ""
      this.commentDialog = true
    },
    async submitComment() {
      if (!this.newCommentText.trim()) return
      this.submittingComment = true // NEU
      try {
        const updated = await api.creditCardBills.addComment(this.commentBill._id, this.newCommentText)
        const idx = this.bills.findIndex((b) => b._id === updated._id)
        if (idx !== -1) {
          updated.previewUrl = this.bills[idx].previewUrl
          this.$set(this.bills, idx, updated)
        }
        this.newCommentText = ""
        this.commentBill = updated // damit Liste im Dialog live aktualisiert
        this.$toast.success("Kommentar hinzugefügt")
      } catch (e) {
        this.$toast.error("Fehler")
      } finally {
        this.submittingComment = false // NEU
      }
    },
    async deleteComment(commentId) {
      try {
        const updated = await api.creditCardBills.deleteComment(this.commentBill._id, commentId)
        const idx = this.bills.findIndex((b) => b._id === updated._id)
        if (idx !== -1) {
          updated.previewUrl = this.bills[idx].previewUrl
          this.$set(this.bills, idx, updated)
        }
        this.commentBill = updated
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async downloadSelected() {
      if (this.selectedIds.length === 0) return
      this.downloadingBulk = true
      try {
        await api.creditCardBills.downloadBulk(this.selectedIds, null, null)
        this.$toast.success(`${this.selectedIds.length} Belege heruntergeladen`)
        this.selectedIds = []
        this.selectionMode = false
      } finally {
        this.downloadingBulk = false
      }
    },
    async downloadAllCurrent() {
      this.downloadingBulk = true
      try {
        const [y, m] = this.filters.month.split("-")
        await api.creditCardBills.downloadBulk(null, m, y)
        this.$toast.success("Alle Belege heruntergeladen")
      } finally {
        this.downloadingBulk = false
      }
    },
    formatDateTime(d) {
      return moment(d).format("DD.MM.YY HH:mm")
    },
    async loadStats() {
      try {
        const [y, m] = this.filters.month.split("-")
        this.stats = await api.creditCardBills.fetchStats({ month: m, year: y })
      } catch (e) {
        console.error("Stats Error", e)
      }
    },
    // ===== ANIMATED COUNTER =====
    animateNumber(target) {
      if (this._animFrame) cancelAnimationFrame(this._animFrame)
      const start = parseFloat(this.animatedTotal) || 0
      const duration = 800
      const startTime = performance.now()
      const tick = (now) => {
        const progress = Math.min((now - startTime) / duration, 1)
        // ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3)
        const current = start + (target - start) * eased
        this.animatedTotal = current.toFixed(2)
        if (progress < 1) this._animFrame = requestAnimationFrame(tick)
      }
      this._animFrame = requestAnimationFrame(tick)
    },

    // ===== MEMORY MANAGEMENT =====
    revokeAllPreviewUrls() {
      if (!this.bills) return
      this.bills.forEach((b) => {
        if (b.previewUrl && b.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(b.previewUrl)
        }
      })
    },

    // ===== LAZY LOAD via IntersectionObserver (v-intersect) =====
    onCardVisible(entries, bill) {
      console.log("HERE")
      // entries kann Array oder Object sein je nach Vuetify Version
      const entry = Array.isArray(entries) ? entries[0] : entries
      if (entry && entry.isIntersecting && !bill.previewUrl && !bill._loading) {
        bill._loading = true
        api.creditCardBills
          .fetchImageBlob(bill._id)
          .then((url) => {
            this.$set(bill, "previewUrl", url)
          })
          .catch(() => {})
          .finally(() => {
            bill._loading = false
          })
      }
    },

    // ===== SEARCH DEBOUNCE =====
    onSearchInput() {
      if (this._searchTimer) clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => this.loadData(), 350)
    },

    // ===== HELPERS =====
    isToday(date) {
      return moment(date).isSame(moment(), "day")
    },
    dataURLtoFile(base64String, filename) {
      const arr = base64String.split(",")
      const b64 = arr.length > 1 ? arr[1] : arr[0]
      const mime = arr.length > 1 ? arr[0].match(/:(.*?);/)[1] : "image/jpeg"
      const bstr = atob(b64)
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      return new File([u8arr], filename, { type: mime })
    },
    async triggerUploadFlow() {
      if (this.$vuetify.breakpoint.xsOnly && window.flutter_inappwebview) {
        try {
          const result = await window.flutter_inappwebview.callHandler("openCameraNative")
          if (result && result.success) {
            const file = this.dataURLtoFile(result.base64, result.name || "camera_upload.jpg")
            this.onFileSelected(file)
          }
        } catch (e) {
          this.$refs.fileInput.click()
        }
      } else {
        this.$refs.fileInput.click()
      }
    },
    confirmDelete(bill) {
      this.billToDelete = bill
      this.deleteDialog = true
    },
    async executeDelete() {
      if (!this.billToDelete) return
      this.deleting = true
      try {
        await api.creditCardBills.delete(this.billToDelete._id)
        // Memory cleanup
        if (this.billToDelete.previewUrl && this.billToDelete.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(this.billToDelete.previewUrl)
        }
        this.bills = this.bills.filter((b) => b._id !== this.billToDelete._id)
        // NEU: Stats parallel laden
        this.loadStats()
        this.$toast.success("Beleg gelöscht")
        this.deleteDialog = false
      } catch (e) {
        this.$toast.error("Fehler beim Löschen")
      } finally {
        this.deleting = false
        this.billToDelete = null
      }
    },
    formatDate(d) {
      return moment(d).format("DD.MM.YY")
    },
    getInitials(u) {
      return u && u.vorname && u.nachname ? (u.vorname[0] + u.nachname[0]).toUpperCase() : "?"
    },
    async loadData() {
      if (!this.uploading) this.loading = true
      this.bills = [] // Liste leeren
      await this.$nextTick() // Warten bis DOM leer ist
      // FIX: Alte Blob URLs aufräumen vor Reload
      this.revokeAllPreviewUrls()
      try {
        const params = {
          search: this.filters.search,
          studioId: this.filters.studioIds,
          signedStatus: this.signedFilter,
        }
        const [y, m] = this.filters.month.split("-")
        params.month = m
        params.year = y
        const data = await api.creditCardBills.fetchAll(params)
        this.bills = data.map((b) => ({ ...b, previewUrl: null, _loading: false }))

        // NEU: Stats parallel laden
        this.loadStats()
        if (this.filters.search && this.filters.search.length > 0) {
          const searchLower = this.filters.search.toLowerCase()
          this.expanded = this.bills.filter(
            (b) => b.lineItems && b.lineItems.some((item) => item.name && item.name.toLowerCase().includes(searchLower)),
          )
        } else {
          this.expanded = []
        }
        // Lazy Load via IntersectionObserver übernimmt das jetzt — kein Bulk-Load mehr
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    openUploadDialog() {
      this.isEditMode = false
      this.aiDetectedDate = false
      this.dateConfirmed = false
      this.editingId = null
      this.aiStep = 1
      this.uploadFile = null
      this.currentUploadPreview = null
      this.mobileTab = 0
      this.newBill = { storeName: "", date: moment().format("YYYY-MM-DD"), studio: null, amount: null, lineItems: [] }
      this.dialog = true
    },
    openEditDialog(bill) {
      this.isEditMode = true
      this.editingId = bill._id
      this.aiDetectedDate = false
      this.dateConfirmed = true // im Edit-Mode ist Datum ja schon mal gespeichert worden
      this.aiStep = 3
      this.mobileTab = 0
      this.uploadFile = null
      this.newBill = JSON.parse(
        JSON.stringify({
          storeName: bill.storeName,
          date: moment(bill.date).format("YYYY-MM-DD"),
          studio: bill.studio ? bill.studio._id : null,
          amount: bill.amount,
          tax: bill.tax,
          lineItems: bill.lineItems || [],
        }),
      )
      this.currentUploadPreview = bill.previewUrl
      this.dialog = true
    },
    async onFileSelected(file) {
      if (!file) return

      if (file.type === "application/pdf") {
        this.uploading = true
        try {
          const pdfjsLib = await this.loadPdfJs()
          const imageDataUrl = await this.convertPdfToImage(file, pdfjsLib)

          // PDF für den späteren Upload speichern (das was ins Backend geht)
          this.uploadFile = file
          // Bild für die Vorschau im Dialog
          this.currentUploadPreview = imageDataUrl

          // Bild-Blob erzeugen, damit die Vision AI ein Bild verarbeiten kann
          const response = await fetch(imageDataUrl)
          const blob = await response.blob()

          // WICHTIG: Den Blob an die Analyse übergeben
          this.startAiAnalysis(blob)
        } catch (e) {
          console.error("PDF Processing Error:", e)
          this.$toast.error("Fehler bei der PDF-Umwandlung")
        } finally {
          this.uploading = false
        }
      } else {
        // Standard-Bild Flow (JPEG/PNG)
        this.uploadFile = file
        this.currentUploadPreview = URL.createObjectURL(file)
        this.startAiAnalysis()
      }
    },
    // WICHTIG: Hier muss 'convertedBlob = null' in die Klammern!
    async startAiAnalysis(convertedBlob = null) {
      // Check ob Datei oder konvertierter Blob da ist
      if (!this.uploadFile && !convertedBlob) return

      this.aiStep = 2
      const self = this
      self.reasoningText =
        "> Initializing Vision Module...\n> Source: " + (convertedBlob ? "PDF (Converted)" : "Image File") + "\n> Connecting to Neural Net...\n\n"

      const fd = new FormData()
      // Wenn convertedBlob existiert (PDF), schicken wir das Bild an die AI.
      // Das Original-PDF bleibt in 'this.uploadFile' für den späteren saveBill Upload.
      fd.append("file", convertedBlob || this.uploadFile)

      try {
        const data = await api.creditCardBills.analyzeStream(fd, (token) => {
          if (token) self.reasoningText += token
          self.$nextTick(() => {
            const terminal = self.$refs.terminalOutput
            if (terminal) terminal.scrollTop = terminal.scrollHeight
          })
        })

        // Zuweisung der AI-Ergebnisse (Dein restlicher Code...)
        if (data.storeName) self.newBill.storeName = data.storeName
        if (data.amount) self.newBill.amount = data.amount
        if (data.tax) self.newBill.tax = data.tax
        if (data.lineItems) self.newBill.lineItems = data.lineItems

        if (data.date) {
          const parsedDate = moment(data.date, ["YYYY-MM-DD", "DD.MM.YYYY", "DD.MM.YY", "D.M.YYYY", "YYYY/MM/DD"], true)
          if (parsedDate.isValid()) {
            self.newBill.date = parsedDate.format("YYYY-MM-DD")
            self.aiDetectedDate = true
            self.dateConfirmed = false
          } else {
            self.newBill.date = moment().format("YYYY-MM-DD")
            self.aiDetectedDate = false
            self.dateConfirmed = true
          }
        } else {
          self.newBill.date = moment().format("YYYY-MM-DD")
          self.aiDetectedDate = false
          self.dateConfirmed = true
        }

        setTimeout(() => {
          self.aiStep = 3
        }, 800)
      } catch (e) {
        console.error("AI Analysis Error:", e)
        self.$toast.error("Analyse fehlgeschlagen.")
        self.aiStep = 3
      }
    },
    addLineItem() {
      this.newBill.lineItems.push({ name: "", quantity: 1, price: null })
    },
    removeLineItem(i) {
      this.newBill.lineItems.splice(i, 1)
    },
    async saveBill() {
      if (!this.$refs.form.validate()) return

      // === DATUMS-CHECK ===
      if (!this.isEditMode && this.aiDetectedDate && !this.dateConfirmed) {
        const ok = await this.confirmDateDialog()
        if (!ok) return
      }

      // Plausi: Datum in der Zukunft?
      if (moment(this.newBill.date).isAfter(moment(), "day")) {
        const ok = confirm(`Achtung: Das Datum (${moment(this.newBill.date).format("DD.MM.YYYY")}) liegt in der Zukunft. Trotzdem speichern?`)
        if (!ok) return
      }

      // Plausi: Datum > 6 Monate alt?
      if (moment(this.newBill.date).isBefore(moment().subtract(6, "months"))) {
        const ok = confirm(
          `Achtung: Das Datum (${moment(this.newBill.date).format("DD.MM.YYYY")}) liegt mehr als 6 Monate zurück. Trotzdem speichern?`,
        )
        if (!ok) return
      }
      this.uploading = true
      try {
        if (this.isEditMode) {
          const payload = {
            storeName: this.newBill.storeName,
            date: this.newBill.date,
            studio: this.newBill.studio,
            amount: parseFloat(this.newBill.amount),
            tax: parseFloat(this.newBill.tax || 0),
            lineItems: this.newBill.lineItems,
          }
          const updatedBill = await api.creditCardBills.update(this.editingId, payload)
          const idx = this.bills.findIndex((b) => b._id === this.editingId)
          if (idx !== -1) {
            updatedBill.previewUrl = this.bills[idx].previewUrl
            this.$set(this.bills, idx, updatedBill)
          }
          this.$toast.success("Aktualisiert")
        } else {
          const fd = new FormData()
          fd.append("file", this.uploadFile)
          fd.append("date", this.newBill.date)
          fd.append("studio", this.newBill.studio)
          fd.append("amount", this.newBill.amount)
          fd.append("storeName", this.newBill.storeName)
          fd.append("lineItems", JSON.stringify(this.newBill.lineItems))
          fd.append("tax", this.newBill.tax || 0)
          const savedBill = await api.creditCardBills.upload(fd)
          savedBill.previewUrl = this.currentUploadPreview
          if (!savedBill.uploader) savedBill.uploader = { vorname: "Ich", nachname: "" }
          const billMonth = moment(savedBill.date).format("YYYY-MM")
          if (billMonth === this.filters.month) this.bills.unshift(savedBill)
          this.$toast.success("Gespeichert")
        }
        this.dialog = false
        this.loadData()
      } catch (e) {
        this.$toast.error("Fehler: " + (e.response?.data || e.message))
      } finally {
        this.uploading = false
      }
    },
    async openPreview(bill) {
      if (!bill.previewUrl) bill.previewUrl = await api.creditCardBills.fetchImageBlob(bill._id)
      this.currentPreviewUrl = bill.previewUrl
      this.previewDialog = true
    },
    // ===== ZOOM / PAN =====
    onImageLoad() {
      this.resetZoom()
    },
    closePreview() {
      this.previewDialog = false
      this.resetZoom()
    },
    resetZoom() {
      this.zoomScale = 1
      this.zoomTranslateX = 0
      this.zoomTranslateY = 0
    },
    zoomIn() {
      this.setZoom(Math.min(this.zoomScale * 1.5, 5))
    },
    setZoom(newScale, originX, originY) {
      const container = this.$refs.zoomContainer
      if (!container) {
        this.zoomScale = newScale
        return
      }
      const rect = container.getBoundingClientRect()
      // Wenn kein Origin angegeben → Mitte
      const ox = originX !== undefined ? originX - rect.left - rect.width / 2 : 0
      const oy = originY !== undefined ? originY - rect.top - rect.height / 2 : 0

      // Damit der Punkt unter dem Cursor / Finger fix bleibt
      const scaleRatio = newScale / this.zoomScale
      this.zoomTranslateX = ox - (ox - this.zoomTranslateX) * scaleRatio
      this.zoomTranslateY = oy - (oy - this.zoomTranslateY) * scaleRatio
      this.zoomScale = newScale
      this.clampTranslate()
    },
    clampTranslate() {
      // Verhindert dass Bild zu weit aus dem Sichtbereich geschoben wird
      const container = this.$refs.zoomContainer
      const img = this.$refs.zoomImage
      if (!container || !img) return
      const cw = container.clientWidth
      const ch = container.clientHeight
      const iw = img.clientWidth * this.zoomScale
      const ih = img.clientHeight * this.zoomScale
      const maxX = Math.max(0, (iw - cw) / 2)
      const maxY = Math.max(0, (ih - ch) / 2)
      this.zoomTranslateX = Math.max(-maxX, Math.min(maxX, this.zoomTranslateX))
      this.zoomTranslateY = Math.max(-maxY, Math.min(maxY, this.zoomTranslateY))
    },
    onWheel(e) {
      const delta = -e.deltaY
      const factor = delta > 0 ? 1.1 : 0.9
      const newScale = Math.max(1, Math.min(5, this.zoomScale * factor))
      this.setZoom(newScale, e.clientX, e.clientY)
    },
    onDoubleClick(e) {
      if (this.zoomScale > 1) {
        this.resetZoom()
      } else {
        this.setZoom(2.5, e.clientX, e.clientY)
      }
    },
    // ----- Touch -----
    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },
    onTouchStart(e) {
      if (e.touches.length === 2) {
        this._isPinching = true
        this._isPanning = false
        this._touchStartDist = this.getTouchDistance(e.touches)
        this._touchStartScale = this.zoomScale
      } else if (e.touches.length === 1 && this.zoomScale > 1) {
        this._isPanning = true
        this._isPinching = false
        this._touchStartX = e.touches[0].clientX
        this._touchStartY = e.touches[0].clientY
        this._touchStartTranslateX = this.zoomTranslateX
        this._touchStartTranslateY = this.zoomTranslateY
      }
    },
    onTouchMove(e) {
      if (this._isPinching && e.touches.length === 2) {
        const newDist = this.getTouchDistance(e.touches)
        const ratio = newDist / this._touchStartDist
        const newScale = Math.max(1, Math.min(5, this._touchStartScale * ratio))
        // Mittelpunkt der beiden Finger als Zoom-Origin
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2
        this.setZoom(newScale, cx, cy)
      } else if (this._isPanning && e.touches.length === 1) {
        const dx = e.touches[0].clientX - this._touchStartX
        const dy = e.touches[0].clientY - this._touchStartY
        this.zoomTranslateX = this._touchStartTranslateX + dx
        this.zoomTranslateY = this._touchStartTranslateY + dy
        this.clampTranslate()
      }
    },
    onTouchEnd(e) {
      if (e.touches.length === 0) {
        this._isPinching = false
        this._isPanning = false
        if (this.zoomScale < 1) this.resetZoom()
      }
    },
    // ----- Mouse Drag (Desktop) -----
    onMouseDown(e) {
      if (this.zoomScale <= 1) return
      e.preventDefault()
      this._isDragging = true
      this._mouseStartX = e.clientX - this.zoomTranslateX
      this._mouseStartY = e.clientY - this.zoomTranslateY
      window.addEventListener("mousemove", this.onMouseMove)
      window.addEventListener("mouseup", this.onMouseUp)
    },
    onMouseMove(e) {
      if (!this._isDragging) return
      this.zoomTranslateX = e.clientX - this._mouseStartX
      this.zoomTranslateY = e.clientY - this._mouseStartY
      this.clampTranslate()
    },
    onMouseUp() {
      this._isDragging = false
      window.removeEventListener("mousemove", this.onMouseMove)
      window.removeEventListener("mouseup", this.onMouseUp)
    },
    downloadFile(bill) {
      const link = document.createElement("a")
      link.href = bill.previewUrl
      link.download = "beleg.webp"
      link.click()
    },
    async downloadExport() {
      this.loadingExport = true
      try {
        const [y, m] = this.filters.month.split("-")
        await api.creditCardBills.downloadPdf(m, y)
      } finally {
        this.loadingExport = false
      }
    },
  },
}
</script>

<style scoped>
.receipt-signed-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(76, 175, 80, 0.4);
}
.receipt-checkbox {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 3;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 2px;
}
.selection-bar {
  background: rgba(25, 118, 210, 0.12) !important;
  border: 1px solid rgba(25, 118, 210, 0.3);
  position: sticky;
  top: 72px;
  z-index: 5;
}

@media (max-width: 600px) {
  .selection-bar {
    top: 64px;
  }
}
.comment-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
/* ============ ROOT / GENERAL ============ */
.cc-root {
  min-height: 100vh;
}
@media (max-width: 960px) {
  .cc-root {
    padding-bottom: 80px !important;
  }
}
.muted-text {
  color: rgba(255, 255, 255, 0.5) !important;
}
.letter-spaced {
  letter-spacing: 1.2px;
}
.font-family-mono {
  font-family: "JetBrains Mono", "Consolas", monospace !important;
}
.position-relative {
  position: relative;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* ============ STATS HERO ============ */
.stats-hero {
  background: linear-gradient(135deg, #1a1a1a 0%, #161616 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;
  overflow: hidden;
}
.stats-hero-glow {
  position: absolute;
  top: -50%;
  left: -10%;
  width: 60%;
  height: 200%;
  background: radial-gradient(ellipse, rgba(25, 118, 210, 0.18) 0%, transparent 60%);
  pointer-events: none;
  filter: blur(30px);
}
.amount-display {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-feature-settings: "tnum";
}
.currency-prefix {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
}
.amount-number {
  background: linear-gradient(135deg, #fff 0%, #b0bec5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-variant-numeric: tabular-nums;
}
.divider-glow {
  background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.15), transparent) !important;
  border: none !important;
  width: 1px;
  height: 60px !important;
}

/* Studio Pills */
.studio-pill {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 10px 14px;
  min-width: 130px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.studio-pill:hover {
  background: rgba(25, 118, 210, 0.08);
  border-color: rgba(25, 118, 210, 0.3);
  transform: translateY(-2px);
}
.studio-pill-name {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
}
.studio-pill-amount {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
}
.studio-pill-bar {
  height: 3px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 6px;
}
.studio-pill-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
  border-radius: 2px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ============ CONTROL BAR ============ */
.control-bar {
  background: rgba(26, 26, 26, 0.8) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: sticky;
  top: 8px;
}
.search-field >>> .v-input__slot {
  transition: background 0.2s;
}
.search-field:hover >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.07) !important;
}
.view-toggle >>> .v-btn {
  background: rgba(255, 255, 255, 0.04) !important;
}
.view-toggle >>> .v-btn--active {
  background: rgba(25, 118, 210, 0.2) !important;
  color: #42a5f5 !important;
}
.date-btn {
  background: rgba(255, 255, 255, 0.04) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  text-transform: none !important;
  letter-spacing: 0;
}
.upload-btn {
  background: linear-gradient(135deg, #1976d2, #1565c0) !important;
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.4) !important;
  text-transform: none !important;
  letter-spacing: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(25, 118, 210, 0.6) !important;
}

/* ============ SKELETON ============ */
.skeleton-card {
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.skeleton-img {
  height: 170px;
  background: #1f1f1f;
  border-radius: 12px 12px 0 0;
}
.skeleton-line {
  height: 12px;
  background: #1f1f1f;
  border-radius: 6px;
}
.shimmer {
  background: linear-gradient(90deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ============ EMPTY STATE ============ */
.empty-state {
  animation: fadeInUp 0.4s ease-out;
}
.empty-icon-wrap {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.03) 0%, transparent 70%);
  display: flex;
  align-items: center;
  justify-content: center;
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============ STAGGER ANIMATION ============ */
.stagger-item {
  opacity: 0;
  animation: staggerIn 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
}
@keyframes staggerIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ============ RECEIPT CARD (Grid) ============ */
.receipt-card {
  background: linear-gradient(180deg, #1a1a1a 0%, #151515 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
}
.receipt-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, transparent 40%, rgba(25, 118, 210, 0.4) 100%);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.4s;
  pointer-events: none;
}
.receipt-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.7), 0 0 30px rgba(25, 118, 210, 0.15);
  border-color: rgba(25, 118, 210, 0.3);
}
.receipt-card:hover::before {
  opacity: 1;
}

.receipt-img-wrap {
  position: relative;
  height: 170px;
  background: #0f0f0f;
  overflow: hidden;
}
.receipt-img {
  transition: transform 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.receipt-card:hover .receipt-img {
  transform: scale(1.06);
}
.receipt-img-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.receipt-img-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 12px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85), transparent);
}
.receipt-studio-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 0.65rem;
  padding: 3px 8px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.amount-line {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.currency-mini {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
}
.amount-mini {
  font-size: 1.4rem;
  font-weight: 800;
  color: #42a5f5;
  font-variant-numeric: tabular-nums;
}
.uploader-avatar {
  background: linear-gradient(135deg, #424242, #212121) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
}
.receipt-actions {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  background: rgba(0, 0, 0, 0.2);
}

.pulse-dot {
  animation: pulseDot 2s ease-in-out infinite;
}
@keyframes pulseDot {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.85);
  }
}

/* ============ MOBILE ROW ============ */
.mobile-row-card {
  background: linear-gradient(135deg, #1a1a1a, #151515) !important;
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.25s ease;
}
.mobile-row-card:active {
  transform: scale(0.98);
  background: #1f1f1f !important;
}
.mobile-thumb {
  background: #0f0f0f !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.mobile-amount {
  font-size: 1.25rem;
  font-weight: 800;
  color: #42a5f5;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

/* ============ TABLE ============ */
.cc-table {
  background: transparent !important;
}
.cc-table >>> th {
  color: rgba(255, 255, 255, 0.5) !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  letter-spacing: 0.8px;
}
.cc-table >>> tr:hover {
  background: rgba(25, 118, 210, 0.06) !important;
}
.cc-table >>> td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
  cursor: pointer;
}
.studio-chip {
  background: rgba(25, 118, 210, 0.15) !important;
  color: #90caf9 !important;
  border: 1px solid rgba(25, 118, 210, 0.3);
}
.line-item-row {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
}
.highlight-match {
  background: rgba(25, 118, 210, 0.25);
  color: #fff;
  padding: 1px 4px;
  border-radius: 3px;
  font-weight: 600;
}

/* ============ FAB ============ */
.fab-btn {
  background: linear-gradient(135deg, #1976d2, #1565c0) !important;
  box-shadow: 0 8px 30px rgba(25, 118, 210, 0.5) !important;
  animation: fabPulse 3s ease-in-out infinite;
}
@keyframes fabPulse {
  0%,
  100% {
    box-shadow: 0 8px 30px rgba(25, 118, 210, 0.5);
  }
  50% {
    box-shadow: 0 8px 40px rgba(25, 118, 210, 0.7);
  }
}

/* ============ UPLOAD ZONE ============ */
.upload-zone {
  min-height: 320px;
  background: radial-gradient(ellipse at center, #1a1a1a 0%, #0f0f0f 100%);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.upload-zone:hover {
  border-color: #1976d2;
  background: radial-gradient(ellipse at center, #1f1f1f 0%, #141414 100%);
  transform: scale(1.01);
}
.circle-bg {
  width: 90px;
  height: 90px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.2), rgba(25, 118, 210, 0.05));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(25, 118, 210, 0.3);
}

/* ============ SCANNER ============ */
.scanner-container {
  position: relative;
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(25, 118, 210, 0.3);
  border-radius: 16px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.05), #0a0a0a);
  overflow: hidden;
}
.scanner-line {
  position: absolute;
  width: 100%;
  height: 3px;
  background: #00e5ff;
  box-shadow: 0 0 20px #00e5ff, 0 0 40px rgba(0, 229, 255, 0.5);
  top: 0;
  animation: scan 2.5s ease-in-out infinite;
  z-index: 2;
}
@keyframes scan {
  0%,
  100% {
    top: 0%;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    top: 100%;
  }
  90% {
    opacity: 1;
  }
}
.terminal-card {
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* ============ INPUTS ============ */
.custom-amount-input >>> input {
  font-size: 2.8rem !important;
  text-align: center;
  font-weight: 800;
  color: #42a5f5 !important;
  font-variant-numeric: tabular-nums;
}
.centered-input >>> input {
  text-align: center;
}

.cursor-blink {
  animation: blink 1s step-end infinite;
  color: #4caf50;
  font-weight: bold;
}
@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

@media (max-width: 600px) {
  .display-1 {
    font-size: 1.8rem !important;
  }
}

/* ============ ZOOM PREVIEW ============ */
.preview-card {
  height: 100vh;
  overflow: hidden;
}
.preview-toolbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  z-index: 10;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent);
  pointer-events: none;
}
.preview-toolbar > * {
  pointer-events: auto;
}
.zoom-container {
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  background: black;
}
.zoom-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center center;
  will-change: transform;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}
</style>
